import styled from '@emotion/styled'
import { ExtranetShopProductListItemDto } from '@pharmac-extranet/extranet-api'
import {
  ExtranetContainer,
  ExtranetListItem,
  ExtranetListItemProps,
  MediaQuery,
  formatDateString,
  htmlStringParser,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import {
  PharmacBadge,
  PharmacButton,
  PharmacContainer,
  PharmacIcon,
  PharmacText,
  PharmacTruncate,
} from '@pharmac/ui'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ConfirmationModal } from '../confirmation-modal/ConfirmationModal'
import ImageNotAvailable from '../dose-dispensing-catalog/image-not-available/NoImageAvailable_16_9.jpg'
import { useShop } from './useShop'

type ProductProps = {
  product: ExtranetShopProductListItemDto
}

const TopBarText = styled(PharmacText)`
  width: max-content;

  ${MediaQuery.maxMobile} {
    padding: var(--spacing-tiny) 0;
  }
`

const Badge = styled(PharmacBadge)<{ isDesktop?: boolean }>`
  padding: ${(p) => (p.isDesktop ? '0 var(--spacing-small)' : undefined)};

  ${MediaQuery.maxMobile} {
    width: fit-content;
  }
`

const ContentText = styled(PharmacText)`
  max-height: 100%;
  overflow: hidden;
`

export const ShopOwnedListItem = ({ product }: ProductProps) => {
  const { t } = useLanguage()
  const navigate = useNavigate()
  const { formatTimecodeToFancyForm, productCategory, deleteSubscription, langLabel } = useShop(
    product.productId
  )
  const { minDesktop, minTablet, maxMobile } = useBreakpoint()
  const [removeSubscription, setRemoveSubscription] = useState<boolean>(false)

  const itemProps: ExtranetListItemProps = {
    isShop: true,
    border: true,
    image: product.image
      ? {
          desktop: product.image.desktopUrl,
          desktopLarge: product.image.desktopLargeUrl,
          mobile: product.image.mobileUrl,
        }
      : {
          desktop: ImageNotAvailable,
          desktopLarge: ImageNotAvailable,
          mobile: ImageNotAvailable,
        },
    mobileLabels: [
      <>
        {maxMobile && (
          <ExtranetContainer
            full
            alignCenter
            flexWrap
            marginLargeBottom
          >
            {product.language?.code && (
              <ExtranetContainer paddingMediumRight>
                <TopBarText>{langLabel(String(product.language?.code))}</TopBarText>
              </ExtranetContainer>
            )}
            <ExtranetContainer paddingMediumRight>
              <TopBarText>{productCategory(product?.productFamily)}</TopBarText>
            </ExtranetContainer>
            <ExtranetContainer paddingMediumRight>
              <TopBarText>
                {t('extranet:shop.published')}: {formatDateString(String(product.publishDate))}
              </TopBarText>
            </ExtranetContainer>
            {product.productFamily === 'recording-product' && product.videoDuration && (
              <ExtranetContainer alignCenter>
                <PharmacIcon
                  size="15px"
                  icon="clock"
                />
                <PharmacText
                  marginSmallLeft
                  paddingMediumRight
                >
                  {formatTimecodeToFancyForm(product.videoDuration)}
                </PharmacText>
              </ExtranetContainer>
            )}
          </ExtranetContainer>
        )}
      </>,
    ],
    title: PharmacTruncate(product.title, 75),
    labels: [
      <>
        {!maxMobile && (
          <ExtranetContainer
            full
            alignCenter
          >
            {product.language?.code && (
              <ExtranetContainer paddingMediumRight>
                <TopBarText>{langLabel(String(product.language?.code))}</TopBarText>
              </ExtranetContainer>
            )}
            <ExtranetContainer paddingMediumRight>
              <TopBarText>{productCategory(product?.productFamily)}</TopBarText>
            </ExtranetContainer>
            <ExtranetContainer paddingMediumRight>
              <TopBarText>
                {t('extranet:shop.published')}: {formatDateString(String(product.publishDate))}
              </TopBarText>
            </ExtranetContainer>
            {product.productFamily === 'recording-product' && product.videoDuration && (
              <ExtranetContainer alignCenter>
                <PharmacIcon
                  size="15px"
                  icon="clock"
                />
                <TopBarText
                  marginSmallLeft
                  paddingMediumRight
                >
                  {formatTimecodeToFancyForm(product.videoDuration)}
                </TopBarText>
              </ExtranetContainer>
            )}
            <PharmacContainer style={{ display: 'flex', overflow: 'hidden' }}>
              {product.tags?.map((a) => (
                <Badge
                  isDesktop
                  key={a.id}
                  small
                  variant="primary"
                  marginSmallRight
                >
                  {a.name}
                </Badge>
              ))}
            </PharmacContainer>
          </ExtranetContainer>
        )}
      </>,
    ],
    content: (
      <ContentText
        dangerouslySetInnerHTML={{
          __html: htmlStringParser(
            PharmacTruncate(product.description, minDesktop ? 460 : minTablet ? 200 : 100) ?? ''
          ),
        }}
      />
    ),
    mobileContent: (
      <>
        {maxMobile && (
          <>
            <ExtranetContainer paddingMediumTop>
              <PharmacContainer style={{ display: 'flex', overflow: 'hidden' }}>
                {product.tags?.map((a) => (
                  <Badge
                    isDesktop={false}
                    key={a.id}
                    small
                    variant="primary"
                    marginSmallRight
                  >
                    {a.name}
                  </Badge>
                ))}
              </PharmacContainer>
            </ExtranetContainer>
            <ExtranetContainer
              full
              marginMediumTop
              style={{ borderTop: '2px solid var(--color-accent-4)' }}
            >
              {product.unitSellingPrice === 0 ? (
                <ExtranetContainer
                  full
                  spaceBetween
                  paddingSmallTop
                >
                  <PharmacButton
                    size="small"
                    variant="secondary"
                    onClick={() => setRemoveSubscription(true)}
                  >
                    <PharmacText weight="semibold">{t('common:remove')}</PharmacText>
                  </PharmacButton>
                  <PharmacButton
                    size="small"
                    marginMediumHorizontal
                    onClick={() => navigate(`${product.productId}`)}
                  >
                    <PharmacText paddingSmallHorizontal>
                      {t('extranet:shop.buttons.goTo')}
                    </PharmacText>
                    <PharmacIcon
                      size="15px"
                      icon="chevronRight"
                    />
                  </PharmacButton>
                </ExtranetContainer>
              ) : (
                <ExtranetContainer
                  full
                  justifyEnd
                  paddingSmallTop
                >
                  <PharmacButton
                    size="small"
                    marginMediumHorizontal
                    onClick={() => navigate(`${product.productId}`)}
                  >
                    <PharmacText paddingSmallHorizontal>
                      {t('extranet:shop.buttons.goTo')}
                    </PharmacText>
                    <PharmacIcon
                      size="15px"
                      icon="chevronRight"
                    />
                  </PharmacButton>
                </ExtranetContainer>
              )}
            </ExtranetContainer>
          </>
        )}
      </>
    ),
    footerContent: (
      <>
        {minTablet && (
          <ExtranetContainer full>
            <ExtranetContainer
              full
              flexRow
              alignCenter
            ></ExtranetContainer>
            {/* TODO: add when info available from erp */}
            {/* <ExtranetContainer
              flexRow
              alignCenter
              paddingLargeBottom
            >
              <>
                <PharmacIcon
                  icon="flag"
                  style={{ color: 'var(--color-primary)' }}
                />
                {!product.accessPeriod?.isUnlimited ? (
                  <>
                    <PharmacText paddingSmallLeft>
                      {t('extranet:shop.product.viewable')}:
                    </PharmacText>
                    <PharmacText paddingSmallLeft>{product.accessPeriod?.duration}</PharmacText>
                    {accessPeriodDuration(product.accessPeriod?.durationUnit)}
                  </>
                ) : (
                  <PharmacText paddingSmallLeft>
                    {t('extranet:shop.product.unlimitedAccess')}
                  </PharmacText>
                )}
              </>
            </ExtranetContainer> */}
            <ExtranetContainer
              alignCenter
              full
              justifyEnd
              paddingMediumVertical={!minTablet}
              marginSmallTop={!minTablet}
              style={{
                borderTop: !minTablet ? '2px solid var(--color-accent-4)' : undefined,
                marginBottom: !maxMobile ? 'var(--spacing-tiny)' : undefined,
              }}
            >
              <ExtranetContainer spaceBetween={!minTablet}>
                <>
                  {product.unitSellingPrice === 0 && (
                    <PharmacButton
                      size="small"
                      variant="secondary"
                      onClick={() => setRemoveSubscription(true)}
                    >
                      <PharmacText weight="semibold">{t('common:remove')}</PharmacText>
                    </PharmacButton>
                  )}
                  <PharmacButton
                    size="small"
                    marginMediumHorizontal
                    onClick={() => navigate(`${product.productId}`)}
                  >
                    <PharmacText paddingSmallHorizontal>
                      {t('extranet:shop.buttons.goTo')}
                    </PharmacText>
                    <PharmacIcon
                      size="15px"
                      icon="chevronRight"
                    />
                  </PharmacButton>
                </>
              </ExtranetContainer>
            </ExtranetContainer>
          </ExtranetContainer>
        )}
      </>
    ),
    navigateFn: () => navigate(`${product.productId}`),
    ariaDescription: 'Link to product',
  }

  return (
    <>
      <ExtranetListItem
        hasMobileLabels
        key={'product-' + product.productId}
        {...itemProps}
      />
      <ConfirmationModal
        title={t('extranet:shop.removeProductModal.title')}
        content={
          <ExtranetContainer full>
            <PharmacText>{t('extranet:shop.removeProductModal.description')}</PharmacText>
          </ExtranetContainer>
        }
        onSubmit={deleteSubscription}
        onClose={() => setRemoveSubscription(false)}
        closeButtonText={t('common:cancel')}
        submitButtonText={t('common:remove')}
        showOnProp={removeSubscription}
      />
    </>
  )
}
